<template>
  <ModalVue
    :modal-active="modalActive"
    :has-header-border="true"
    :has-footer-border="true"
    max-width="max-w-lg"
    no-body-padding
    @close="handleClose"
    data-testid="add-doctor-modal"
  >
    <template #header>
      <p class="font-bold text-lg text-left">Adicionar médico</p>
    </template>

    <div class="flex flex-col gap-4 p-4">
      <SearchInput
        v-model="searchText"
        width="100%"
        label="Buscar médico"
        hide-details
        density="compact"
        data-testid="add-doctor-search-input"
      />

      <div v-if="isAvailableDoctorsLoading" class="flex items-center justify-center py-12">
        <v-progress-circular indeterminate color="primary" />
      </div>

      <div
        v-else-if="availableDoctors.length > 0"
        class="flex flex-col max-h-[260px] overflow-y-auto"
      >
        <label
          v-for="doctor in availableDoctors"
          :key="doctor.id"
          class="flex items-center gap-3 px-3 py-3 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
          :data-testid="`select-doctor-${doctor.id}`"
        >
          <input
            type="checkbox"
            :value="doctor.id"
            v-model="selectedDoctorIds"
            class="checkbox shrink-0"
          />
          <v-avatar size="36" color="primary">
            <ProtectedAvatar :src="resolveAvatarSrc(doctor.avatarUrl)" :size="36">
              <img
                src="@/assets/icons/doctorAvatar.svg"
                alt="Avatar do médico"
                class="w-full h-full rounded-full object-cover"
              />
            </ProtectedAvatar>
          </v-avatar>
          <div class="text-left min-w-0 flex-1">
            <div class="flex items-center gap-2">
              <p class="font-semibold text-sm text-black truncate">
                {{ formatDoctorName(doctor.name) }}
              </p>
              <LabelBadge
                v-if="doctor.isOutOfScale"
                label="Fora da escala"
                color="orange"
                size="small"
                :icon="null"
                class="shrink-0 whitespace-nowrap"
                data-testid="badge-out-of-scale"
              />
            </div>
            <p class="text-xs text-gray-500">CRM: {{ doctor.crm }}-{{ doctor.crmUf }}</p>
          </div>
        </label>
      </div>

      <EmptyList v-else title="Nenhum médico encontrado" class="py-12" />
    </div>

    <template #footer>
      <div class="flex items-center justify-between gap-4">
        <OutlineButton
          text="Cancelar"
          width="50%"
          data-testid="cancel-add-doctor-button"
          @click="handleClose"
        />
        <FilledButton
          :text="addButtonLabel"
          width="50%"
          :disabled="selectedDoctorIds.length === 0 || isAdding"
          class="!bg-primary30"
          data-testid="confirm-add-doctor-button"
          @click="handleAdd"
        />
      </div>
    </template>
  </ModalVue>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'

import { useErrorToast } from '@/composables/useErrorToast'
import { useQualificationDocumentalPolicyStore } from '@/pinia-store/useQualificationDocumentalPolicyStore'
import type { AvailableDoctorResponse } from '@/Network/Types/Responses/QualificationTransferResponses'
import { resolveAvatarSrc } from '@/utils/avatar'
import { formatDoctorName } from '@/utils/string'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import SearchInput from '@/components/designSystem/Inputs/SearchInput.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import EmptyList from '@/components/EmptyList.vue'
import ProtectedAvatar from '@/components/designSystem/AvatarUser/ProtectedAvatar.vue'
import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'

interface DoctorOption {
  id: number
  name: string
  crm: string
  crmUf: string
  avatarUrl: string | null
  isOutOfScale: boolean
}

const props = defineProps<{
  modalActive: boolean
  policyId: number
}>()

const emit = defineEmits<{
  close: []
  add: [doctorIds: number[]]
}>()

const { notifyError } = useErrorToast()
const store = useQualificationDocumentalPolicyStore()
const { availableDoctors: storeAvailableDoctors, isAvailableDoctorsLoading } = storeToRefs(store)

const searchText = ref('')
const selectedDoctorIds = ref<number[]>([])
const isAdding = ref(false)

const allDoctors = computed(() => mapDoctorsResponse(storeAvailableDoctors.value))

const availableDoctors = computed(() => {
  if (!searchText.value) return allDoctors.value
  const query = searchText.value.toLowerCase()
  return allDoctors.value.filter(
    (d) => d.name.toLowerCase().includes(query) || d.crm.toLowerCase().includes(query)
  )
})

const addButtonLabel = computed(() => {
  if (selectedDoctorIds.value.length === 0) return 'Adicionar'
  return `Adicionar (${selectedDoctorIds.value.length})`
})

function mapDoctorsResponse(data: AvailableDoctorResponse[]): DoctorOption[] {
  return data.map((item) => ({
    id: item.id,
    name: item.doctorName,
    crm: item.crm,
    crmUf: item.crmUf,
    avatarUrl: item.avatarUrl,
    isOutOfScale: item.isOutOfScale ?? false
  }))
}

async function loadAvailableDoctors() {
  try {
    await store.fetchAvailableDoctors(props.policyId, { limit: 500 })
  } catch (e) {
    notifyError(e, 'Erro ao carregar médicos disponíveis.')
  }
}

watch(
  () => props.modalActive,
  (active) => {
    if (active) {
      searchText.value = ''
      selectedDoctorIds.value = []
      loadAvailableDoctors()
    }
  }
)

function handleClose() {
  emit('close')
}

async function handleAdd() {
  if (selectedDoctorIds.value.length === 0) return
  isAdding.value = true
  try {
    emit('add', [...selectedDoctorIds.value])
  } finally {
    isAdding.value = false
  }
}
</script>
